import { action, makeObservable, observable, runInAction } from 'mobx';
import { notifyError, notifySuccess } from '../../services/notifications';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { WindowState } from '../overlays/windowState';
import { input, InputState } from '..';
import isEmail from 'validator/lib/isEmail';
import { GetDownloadLinkInput } from '@clipr/lib';

export class DownloadJobWindowState
  extends StoreNode {

  readonly nodeType = 'DownloadJobWindow';

  constructor(store: Store) {
    super(store);
    makeObservable<this, 'windowListener'>(this, {
      isLoading: observable,
      submit: action,
      windowListener: action,
      open: action,
      close: action,
    });

    this.window.listen(
      this.windowListener);

    this.emailInput = input(this, {
      name: 'email',
      isRequired: false,
      placeholder: 'Email Address',
      error: input => {
        if (input.isRequired && input.isEmpty)
          return 'Required field';

        if (!input.isEmpty && !isEmail(input.value!.trim()))
          return 'Invalid email address format';

        return null;
      }
    });
  }

  readonly window = new WindowState(this.store);
  readonly emailInput: InputState;

  isLoading: boolean = false;
  jobId: string | null = null;
  teamId: string | null = null;
  startTime: number | null = null
  endTime: number | null = null;

  async submit() {
    if (!this.jobId) {
      notifyError(this, 'Could not start the download.');
      return this.handleSubmitFinally();
    }

    this.isLoading = true;

    const input: GetDownloadLinkInput = {
      jobId: this.jobId,
      teamId: this.teamId ?? undefined,
      startTime: this.startTime ?? undefined,
      endTime: this.endTime ?? undefined,
      email: this.emailInput.value ?? undefined
    }

    const [, err] = await this.store.apiService.getDownloadLink({ args: input });

    if (err) {
      notifyError(this, 'Download failed. Please try again.');
      return this.handleSubmitFinally();
    }

    runInAction(() => {
      notifySuccess(this, 'Email sent successfully.');
      this.handleSubmitFinally();
    });
  }

  handleSubmitFinally() {
    runInAction(() => {
      this.isLoading = false;
      this.close();
    });
  }

  private windowListener = (msg: Message<WindowState>) => {
    switch (msg.type) {
      case 'close':
      case 'outsideClick':
        this.close();
        break;
    }
  }

  open({ jobId, teamId, startTime, endTime }: { jobId: string, teamId?: string, startTime?: number, endTime?: number }) {
    this.dispatch('Overlays', 'openWindow', { name: 'DownloadJobWindow' });

    this.jobId = jobId;
    this.teamId = teamId ?? null;
    this.startTime = startTime ?? null;
    this.endTime = endTime ?? null;
  }

  close() {
    this.jobId = null;
    this.teamId = null;
    this.startTime = null;
    this.endTime = null;
    this.emailInput.clear();

    this.emit('close');
    this.dispatch('Overlays', 'closeWindow');
  }
}